import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import { isSignedIn } from '../auth';
import { createComment, createReaction, deleteComment, deletePost, deleteReaction, editComment, editPost, fetchPostsNextBatch, setMessage, setShouldAskToSignIn } from '../store';
import { sortDesc } from '../utils';
import FeedElem from './feed-elem';
import MultipleSpinners from './multiple-spinners';
import PostModal from './post-modal';
import '../styles/feed-container.scss'

function FeedContainer({ isProfileScreen }) {
  const dispatch = useDispatch()
  const crtUser = useSelector(state => state.crtUser)
  const [postToEdit, setPostToEdit] = useState(null)
  const [postText, setPostText] = useState('')
  const [isPostModalOpen, setIsPostModalOpen] = useState(false)
  const { posts, isLoading, isLoadingMorePosts, isProcessingReaction, areThereMorePosts } = useSelector(state => state.posts)
  const { usersMap, isUsersLoading } = useSelector(state => state.users)

  function handleLoadMorePosts() {
    if (posts.length) {
      dispatch(fetchPostsNextBatch({
        lastCreatedAt: posts.slice(-1)[0].createdAt,
        userId: isProfileScreen ? crtUser.uid : null
      }))
    }
  }

  function handleDeletePost(postId) {
    dispatch(deletePost({ postId }))
      .unwrap()
      .catch(() => {
        dispatch(setMessage({
          text: 'Something went wrong while deleting your post. Please try again!',
          status: 'error'
        }))
      })
  }

  function handleEditPost(postId) {
    const post = posts.find(_ => _.id === postId)

    if (post) {
      setPostText(post.text)
      setPostToEdit(post)
      setIsPostModalOpen(true)
    }
  }

  function handleConfirmEdit() {
    setIsPostModalOpen(false)

    if (postToEdit) {
      dispatch(editPost({ postId: postToEdit.id, text: postText }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({
            text: 'Your post has been updated successfully!',
            status: 'success'
          }))
        })
        .catch(() => {
          dispatch(setMessage({
            text: 'Something went wrong while updating your post. Please try again!',
            status: 'error'
          }))
        })
    }
  }

  function handleCreateComment(postId, text, postAuthorId) {
    if (isSignedIn()) {
      return dispatch(createComment({ postId, userId: crtUser.uid, text, postAuthorId }))
        .unwrap()
        .catch(() => {
          dispatch(setMessage({
            text: 'Something went wrong while creating your comment. Please try again!',
            status: 'error'
          }))
        })
    } else {
      dispatch(setShouldAskToSignIn({ action: 'comment' }))
    }
  }

  function handleEditComment(commentId, postId, text) {
    return dispatch(editComment({ commentId, postId, text }))
      .unwrap()
      .catch(() => {
        dispatch(setMessage({
          text: 'Something went wrong while updating your comment. Please try again!',
          status: 'error'
        }))
      })
  }

  function handleDeleteComment(commentId, postId) {
    return dispatch(deleteComment({ commentId, postId }))
      .unwrap()
      .catch(() => {
        dispatch(setMessage({
          text: 'Something went wrong while deleting your comment. Please try again!',
          status: 'error'
        }))
      })
  }

  function handleCreateReaction(postId, postAuthorId) {
    if (isSignedIn()) {
      return dispatch(createReaction({ postId, userId: crtUser.uid, postAuthorId }))
        .unwrap()
        .catch(() => {
          dispatch(setMessage({
            text: 'Something went wrong while creating your reaction. Please try again!',
            status: 'error'
          }))
        })
    } else {
      dispatch(setShouldAskToSignIn({ action: 'comment' }))
    }
  }

  function handleDeleteReaction(reactionId, postId) {
    return dispatch(deleteReaction({ reactionId, postId }))
      .unwrap()
      .catch(() => {
        dispatch(setMessage({
          text: 'Something went wrong while deleting your reaction. Please try again!',
          status: 'error'
        }))
      })
  }

  return (
    <div className={'feed-container ' + (isLoading ? ' loading ' : '') + (isProfileScreen ? ' in-profile-screen ' : '')}>
      {isLoading && <MultipleSpinners />}
      {!isLoading && sortDesc(posts).map(post => (
        <FeedElem
          key={post.id}
          post={post}
          onEditPost={handleEditPost}
          onDeletePost={() => handleDeletePost(post.id)}
          onCreateComment={handleCreateComment}
          onEditComment={handleEditComment}
          onDeleteComment={handleDeleteComment}
          crtUser={crtUser}
          usersMap={usersMap}
          isUsersLoading={isUsersLoading}
          onReact={handleCreateReaction}
          onUndoReaction={handleDeleteReaction}
          isProcessingReaction={isProcessingReaction}
        />
      ))}

      {!isLoading && <div className='more-posts-cont'>
        {isLoadingMorePosts && <MultipleSpinners size="sm" />}
        {(!isLoadingMorePosts && areThereMorePosts && !!posts.length) && <Button color='link' onClick={handleLoadMorePosts}>Load More</Button>}
        {(!areThereMorePosts && !!posts.length) && <p>There are no more posts!</p>}
        {(posts.length === 0) && <p>You did not create any posts yet!</p>}
      </div>}

      <PostModal
        isOpen={isPostModalOpen}
        imageUrl={postToEdit && postToEdit.imageUrl}
        text={postText}
        isNewPost={false}
        onTextChange={evt => setPostText(evt.target.value)}
        onConfirm={handleConfirmEdit}
        onCancel={() => setIsPostModalOpen(!isPostModalOpen)}
        onToggle={() => setIsPostModalOpen(!isPostModalOpen)}
      />
    </div>
  )
}

export default FeedContainer
